/**
 * Corvid Courier Frontend Application
 *
 * Secure, minimal frontend for the Suno Gateway API.
 * Modern audio player with playlist UI.
 */

import { ApiClient, ApiClientError } from "./services/api-client.js";
import type { StatusResponse, ClipInfo, LibraryTrack } from "./types/api.js";
import { initStegoPanel, updateCompletedTasks } from "./components/stego-panel.js";
import { startJobPoller } from "./services/job-poller.js";
import type { JobsDiff, JobSnapshot } from "./services/job-poller.js";

// Configuration
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://ctf-suno-gateway.drip-6c1.workers.dev";

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 120;

// SVG Icons
const ICON_PLAY = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="6 3 20 12 6 21 6 3"/></svg>`;
const ICON_PAUSE = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="5" y="4" width="4" height="16" rx="1"/><rect x="15" y="4" width="4" height="16" rx="1"/></svg>`;
const ICON_PREV = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="19 20 9 12 19 4 19 20"/><rect x="5" y="4" width="3" height="16"/></svg>`;
const ICON_NEXT = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 4 15 12 5 20 5 4"/><rect x="16" y="4" width="3" height="16"/></svg>`;
const ICON_KEBAB = `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>`;
const ICON_DOWNLOAD = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
const ICON_LOCK = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
const ICON_TRASH = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;
const ICON_COVER_PLACEHOLDER = `<svg class="cover-placeholder" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>`;
const ICON_IMAGE = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>`;
const ICON_IMPORT = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;

// State
interface Task {
  id: string;
  prompt: string;
  status: StatusResponse["status"];
  clips: ClipInfo[];
  pollCount: number;
  pollTimer?: number;
  hasAudio?: boolean;
  hasCover?: boolean;
  stego?: {
    technique: string;
    payload_size: number;
    encoded_at: string;
  } | null;
}

const state = {
  connected: false,
  tasks: new Map<string, Task>(),
};

const playerState = {
  currentTrackId: null as string | null,
  isPlaying: false,
  blobUrlCache: new Map<string, string>(),
  coverUrlCache: new Map<string, string>(),
  menuOpenFor: null as string | null,
};

const libraryState = {
  tracks: [] as LibraryTrack[],
  page: 0,
  hasMore: true,
  loading: false,
  loaded: false,
  activeTab: "playlist" as "playlist" | "library",
  importedMap: new Map<string, string>(), // suno_clip_id -> gateway task_id
  blobUrlCache: new Map<string, string>(), // suno_clip_id -> blob URL
};

// API Client
const api = new ApiClient(API_BASE_URL);

// Audio engine - single Audio element, never in DOM
let audioEl: HTMLAudioElement;
let rafId: number | null = null;

function createAudioElement(): HTMLAudioElement {
  const audio = new Audio();
  audio.addEventListener("play", () => {
    playerState.isPlaying = true;
    updatePlayButton();
    startProgressLoop();
  });
  audio.addEventListener("pause", () => {
    playerState.isPlaying = false;
    updatePlayButton();
    stopProgressLoop();
  });
  audio.addEventListener("ended", () => {
    playerState.isPlaying = false;
    updatePlayButton();
    stopProgressLoop();
    nextTrack();
  });
  audio.addEventListener("loadedmetadata", () => {
    updateProgress();
  });
  return audio;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function startProgressLoop(): void {
  stopProgressLoop();
  function loop() {
    updateProgress();
    rafId = requestAnimationFrame(loop);
  }
  rafId = requestAnimationFrame(loop);
}

function stopProgressLoop(): void {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

function updateProgress(): void {
  const fill = document.getElementById("progress-fill");
  const currentTimeEl = document.getElementById("current-time");
  const totalTimeEl = document.getElementById("total-time");
  if (!fill || !currentTimeEl || !totalTimeEl) return;

  const current = audioEl.currentTime || 0;
  const duration = audioEl.duration || 0;
  const pct = duration > 0 ? (current / duration) * 100 : 0;

  fill.style.width = `${pct}%`;
  currentTimeEl.textContent = formatTime(current);
  totalTimeEl.textContent = formatTime(duration);
}

function updatePlayButton(): void {
  const btn = document.getElementById("play-btn");
  if (!btn) return;
  btn.innerHTML = playerState.isPlaying ? ICON_PAUSE : ICON_PLAY;
  btn.title = playerState.isPlaying ? "Pause" : "Play";
}

function getPlayableTasks(): Task[] {
  return Array.from(state.tasks.values()).filter(
    (t) => t.status === "complete" && t.hasAudio,
  );
}

async function playTrack(taskId: string): Promise<void> {
  const task = state.tasks.get(taskId);
  if (!task || task.status !== "complete" || !task.hasAudio) return;

  playerState.currentTrackId = taskId;

  // Check blob cache
  let blobUrl = playerState.blobUrlCache.get(taskId);
  if (!blobUrl) {
    try {
      const blob = await api.stream(taskId);
      blobUrl = URL.createObjectURL(blob);
      playerState.blobUrlCache.set(taskId, blobUrl);
    } catch (err) {
      if (err instanceof ApiClientError) {
        showError(`Playback failed: ${err.message}`);
      } else {
        showError("Playback failed.");
      }
      return;
    }
  }

  audioEl.src = blobUrl;
  audioEl.play();
  renderTasks();
}

function togglePlayPause(): void {
  if (!playerState.currentTrackId) return;
  if (playerState.isPlaying) {
    audioEl.pause();
  } else {
    audioEl.play();
  }
}

function nextTrack(): void {
  const playable = getPlayableTasks();
  if (playable.length === 0) return;
  const currentIdx = playable.findIndex(
    (t) => t.id === playerState.currentTrackId,
  );
  const nextIdx = (currentIdx + 1) % playable.length;
  const next = playable[nextIdx];
  if (next) playTrack(next.id);
}

function prevTrack(): void {
  const playable = getPlayableTasks();
  if (playable.length === 0) return;
  const currentIdx = playable.findIndex(
    (t) => t.id === playerState.currentTrackId,
  );
  const prevIdx = currentIdx <= 0 ? playable.length - 1 : currentIdx - 1;
  const prev = playable[prevIdx];
  if (prev) playTrack(prev.id);
}

function handleProgressClick(e: MouseEvent): void {
  const bar = document.getElementById("progress-bar");
  if (!bar || !playerState.currentTrackId || !audioEl.duration) return;
  const rect = bar.getBoundingClientRect();
  const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  audioEl.currentTime = pct * audioEl.duration;
  updateProgress();
}

// Kebab menu
function openMenu(taskId: string, anchorEl: HTMLElement): void {
  closeMenu();
  playerState.menuOpenFor = taskId;

  const task = state.tasks.get(taskId);
  if (!task) return;

  const menu = document.createElement("div");
  menu.className = "track-menu";
  menu.id = "track-menu";

  const isComplete = task.status === "complete";

  if (isComplete) {
    const dlBtn = document.createElement("button");
    dlBtn.className = "track-menu-item";
    dlBtn.innerHTML = `${ICON_DOWNLOAD} Download MP3`;
    dlBtn.addEventListener("click", () => {
      closeMenu();
      downloadTask(taskId);
    });
    menu.appendChild(dlBtn);

    if (task.stego) {
      const stegoBtn = document.createElement("button");
      stegoBtn.className = "track-menu-item";
      stegoBtn.innerHTML = `${ICON_LOCK} Download Encoded`;
      stegoBtn.addEventListener("click", () => {
        closeMenu();
        downloadStegoTask(taskId);
      });
      menu.appendChild(stegoBtn);
    }

    const coverBtn = document.createElement("button");
    coverBtn.className = "track-menu-item";
    coverBtn.innerHTML = `${ICON_IMAGE} Change Cover`;
    coverBtn.addEventListener("click", () => {
      closeMenu();
      openCoverUpload(taskId);
    });
    menu.appendChild(coverBtn);
  }

  const deleteBtn = document.createElement("button");
  deleteBtn.className = "track-menu-item track-menu-item-danger";
  deleteBtn.innerHTML = `${ICON_TRASH} Delete`;
  deleteBtn.addEventListener("click", () => {
    closeMenu();
    deleteTask(taskId);
  });
  menu.appendChild(deleteBtn);

  // Position using fixed coords relative to viewport
  const rect = anchorEl.getBoundingClientRect();
  menu.style.top = `${rect.bottom + 4}px`;
  menu.style.right = `${window.innerWidth - rect.right}px`;

  document.body.appendChild(menu);
}

function closeMenu(): void {
  playerState.menuOpenFor = null;
  document.getElementById("track-menu")?.remove();
}

// LocalStorage keys
const STORAGE_KEYS = {
  tasks: "suno_tasks",
  apiKey: "suno_api_key",
};

// Persistence functions
function saveTasks(): void {
  const tasksArray = Array.from(state.tasks.values()).map((task) => ({
    ...task,
    pollTimer: undefined,
  }));
  try {
    localStorage.setItem(STORAGE_KEYS.tasks, JSON.stringify(tasksArray));
  } catch {
    // Storage full or unavailable
  }
}

function loadTasks(): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.tasks);
    if (stored) {
      const tasksArray = JSON.parse(stored) as Task[];
      tasksArray.forEach((task) => {
        if (!state.tasks.has(task.id)) {
          state.tasks.set(task.id, { ...task, pollTimer: undefined });
        }
      });
    }
  } catch {
    // Invalid data
  }
}

function saveApiKey(key: string): void {
  try {
    localStorage.setItem(STORAGE_KEYS.apiKey, key);
  } catch {
    // Storage unavailable
  }
}

function loadApiKey(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.apiKey);
  } catch {
    return null;
  }
}

function clearApiKey(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.apiKey);
  } catch {
    // Ignore
  }
}

// DOM Elements
const $ = <T extends HTMLElement>(id: string): T =>
  document.getElementById(id) as T;

const elements = {
  apiKeyForm: $<HTMLFormElement>("api-key-form"),
  apiKeyInput: $<HTMLInputElement>("api-key-input"),
  connectBtn: $<HTMLButtonElement>("connect-btn"),
  connectionDot: $<HTMLElement>("connection-dot"),
  connectionText: $<HTMLElement>("connection-text"),
  quotaDisplay: $<HTMLElement>("quota-display"),
  quotaValue: $<HTMLElement>("quota-value"),
  quotaProgress: $<HTMLElement>("quota-progress"),
  quotaDivider: $<HTMLElement>("quota-display-divider"),
  stegoCard: $<HTMLElement>("stego-card"),
  generateCard: $<HTMLElement>("generate-card"),
  generateForm: $<HTMLFormElement>("generate-form"),
  promptInput: $<HTMLTextAreaElement>("prompt-input"),
  styleInput: $<HTMLInputElement>("style-input"),
  generateBtn: $<HTMLButtonElement>("generate-btn"),
  simpleModeTab: $<HTMLButtonElement>("simple-mode-tab"),
  customModeTab: $<HTMLButtonElement>("custom-mode-tab"),
  simpleModeGroup: $<HTMLElement>("simple-mode-group"),
  customModeGroup: $<HTMLElement>("custom-mode-group"),
  titleInput: $<HTMLInputElement>("title-input"),
  lyricsInput: $<HTMLTextAreaElement>("lyrics-input"),
  customStyleInput: $<HTMLInputElement>("custom-style-input"),
  instrumentalCheck: $<HTMLInputElement>("instrumental-check"),
  modelSelect: $<HTMLSelectElement>("model-select"),
  advancedOptions: $<HTMLDetailsElement>("advanced-options"),
  negativeTagsInput: $<HTMLInputElement>("negative-tags-input"),
  vocalGenderNone: $<HTMLInputElement>("vocal-gender-none"),
  vocalGenderM: $<HTMLInputElement>("vocal-gender-m"),
  vocalGenderF: $<HTMLInputElement>("vocal-gender-f"),
  weirdnessSlider: $<HTMLInputElement>("weirdness-slider"),
  weirdnessValue: $<HTMLElement>("weirdness-value"),
  tasksCard: $<HTMLElement>("tasks-card"),
  tasksContainer: $<HTMLElement>("tasks-container"),
  libraryContainer: $<HTMLElement>("library-container"),
  playlistTabBtn: $<HTMLButtonElement>("playlist-tab-btn"),
  libraryTabBtn: $<HTMLButtonElement>("library-tab-btn"),
  errorMessage: $<HTMLElement>("error-message"),
  errorText: $<HTMLElement>("error-text"),
  toastDismiss: $<HTMLButtonElement>("toast-dismiss"),
  lyricsWizardBtn: $<HTMLButtonElement>("lyrics-wizard-btn"),
  styleWizardBtn: $<HTMLButtonElement>("style-wizard-btn"),
};

// Curated Suno-compatible style tags for "I'm Feeling Lucky"
const STYLE_TAGS = {
  genres: [
    "pop", "rock", "hip-hop", "r&b", "jazz", "blues", "country", "folk",
    "electronic", "house", "techno", "trance", "drum and bass", "dubstep",
    "ambient", "lo-fi", "chillwave", "synthwave", "vaporwave", "retrowave",
    "punk", "post-punk", "grunge", "metal", "death metal", "doom metal",
    "indie rock", "indie pop", "shoegaze", "dream pop", "bedroom pop",
    "soul", "funk", "disco", "gospel", "reggae", "ska", "dancehall",
    "latin", "bossa nova", "salsa", "afrobeat", "k-pop", "j-pop",
    "classical", "baroque", "orchestral", "cinematic", "epic",
    "trap", "boom bap", "phonk", "grime", "drill",
    "emo", "screamo", "hardcore", "math rock", "prog rock",
    "new wave", "darkwave", "industrial", "noise",
    "bluegrass", "americana", "celtic", "world music",
    "garage rock", "surf rock", "psychedelic rock", "stoner rock",
    "trip-hop", "downtempo", "breakbeat", "jungle",
    "edm", "future bass", "hardstyle", "happy hardcore",
  ],
  moods: [
    "melancholic", "euphoric", "dreamy", "aggressive", "chill", "upbeat",
    "dark", "ethereal", "nostalgic", "energetic", "haunting", "triumphant",
    "romantic", "playful", "intense", "soothing", "groovy", "brooding",
    "whimsical", "anthemic", "hypnotic", "raw", "lush", "gritty",
    "atmospheric", "uplifting", "somber", "fierce", "tender", "eerie",
  ],
  descriptors: [
    "acoustic", "electric", "distorted", "clean", "reverb-heavy",
    "minimalist", "lush", "layered", "sparse", "polished", "raw",
    "catchy", "complex", "driving", "slow", "fast", "heavy", "light",
    "smooth", "punchy", "bouncy", "swinging", "soaring", "grinding",
    "glitchy", "warm", "cold", "crisp", "fuzzy", "saturated",
  ],
  instruments: [
    "piano", "guitar", "synth", "strings", "brass", "saxophone",
    "violin", "cello", "flute", "organ", "bass guitar", "drum machine",
    "808", "analog synth", "modular synth", "theremin", "harpsichord",
    "slide guitar", "banjo", "mandolin", "ukulele", "sitar", "tabla",
    "steel drums", "marimba", "harp", "accordion", "harmonica",
  ],
  vocals: [
    "female vocals", "male vocals", "falsetto", "raspy vocals",
    "whispered vocals", "powerful vocals", "soulful vocals",
    "choir", "harmonies", "vocoder", "auto-tune",
  ],
  eras: [
    "60s", "70s", "80s", "90s", "2000s", "modern", "retro", "vintage",
    "futuristic", "timeless", "old school", "contemporary",
  ],
};

function randomStyleTags(): string {
  const pick = <T>(arr: T[], n: number): T[] => {
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, n);
  };

  const tags = [
    ...pick(STYLE_TAGS.genres, 1 + Math.floor(Math.random() * 2)),
    ...pick(STYLE_TAGS.moods, 1),
    ...pick(STYLE_TAGS.descriptors, Math.random() > 0.5 ? 1 : 0),
    ...pick(STYLE_TAGS.instruments, Math.random() > 0.4 ? 1 : 0),
    ...pick(STYLE_TAGS.vocals, Math.random() > 0.6 ? 1 : 0),
    ...pick(STYLE_TAGS.eras, Math.random() > 0.6 ? 1 : 0),
  ];

  return tags.join(", ");
}

let toastDismissTimer: number | undefined;
let generateMode: "simple" | "custom" = "simple";
let stopJobPoller: (() => void) | null = null;

function setGenerateMode(mode: "simple" | "custom"): void {
  generateMode = mode;
  const isSimple = mode === "simple";

  elements.simpleModeTab.classList.toggle("active", isSimple);
  elements.customModeTab.classList.toggle("active", !isSimple);
  elements.simpleModeGroup.classList.toggle("hidden", !isSimple);
  elements.customModeGroup.classList.toggle("hidden", isSimple);

  // Toggle required on prompt (only required in simple mode)
  elements.promptInput.required = isSimple;
}

// Utility functions
function showError(message: string): void {
  if (toastDismissTimer) clearTimeout(toastDismissTimer);
  elements.errorText.textContent = message;
  elements.errorMessage.classList.remove("hidden");
  elements.errorMessage.style.animation = "none";
  void elements.errorMessage.offsetHeight;
  elements.errorMessage.style.animation = "";
  toastDismissTimer = window.setTimeout(() => {
    elements.errorMessage.classList.add("hidden");
  }, 6000);
}

function clearError(): void {
  if (toastDismissTimer) clearTimeout(toastDismissTimer);
  elements.errorMessage.classList.add("hidden");
}

function setConnected(connected: boolean): void {
  state.connected = connected;
  elements.connectionDot.classList.toggle("connected", connected);
  elements.connectionDot.classList.toggle("disconnected", !connected);
  elements.connectionText.textContent = connected ? "Online" : "Offline";
  elements.generateCard.classList.toggle("hidden", !connected);
  elements.stegoCard.classList.toggle("hidden", !connected);
  elements.tasksCard.classList.toggle("hidden", !connected);
  elements.quotaDisplay.classList.toggle("hidden", !connected);
  elements.quotaDivider.classList.toggle("hidden", !connected);
  elements.connectBtn.textContent = connected ? "Disconnect" : "Connect";
  elements.apiKeyInput.disabled = connected;
}

function formatStatus(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function getStatusClass(status: string): string {
  switch (status) {
    case "complete":
    case "streaming":
      return "status-complete";
    case "processing":
      return "status-processing";
    case "error":
      return "status-error";
    default:
      return "status-pending";
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function updateQuota(remaining: number, limit: number): void {
  elements.quotaValue.textContent = `${remaining.toLocaleString()} / ${limit.toLocaleString()}`;
  const percentage = limit > 0 ? (remaining / limit) * 100 : 0;
  elements.quotaProgress.style.width = `${percentage}%`;
}

function getCompletedTasks(): { id: string; prompt: string }[] {
  return Array.from(state.tasks.values())
    .filter((t) => t.status === "complete")
    .map((t) => ({ id: t.id, prompt: t.prompt }));
}

function getCompletedTasksForStego(): { id: string; prompt: string }[] {
  const gateway = getCompletedTasks();
  // When library tab is active, also offer un-imported library tracks
  if (libraryState.activeTab === "library") {
    const library = libraryState.tracks
      .filter((t) => !libraryState.importedMap.has(t.id))
      .map((t) => ({
        id: `library:${t.id}`,
        prompt: t.title,
      }));
    return [...gateway, ...library];
  }
  return gateway;
}

// Render functions
function renderNowPlaying(task: Task | null | undefined): string {
  const hasTrack = !!task;
  const disabledAttr = hasTrack ? "" : " disabled";

  const title = task ? escapeHtml(task.prompt) : "No track selected";
  const meta = task
    ? `<span class="now-playing-meta">${task.id.slice(0, 8)}${
        task.stego
          ? ` <span class="badge-stego">${escapeHtml(task.stego.technique)}</span>`
          : ""
      }</span>`
    : "";

  const currentTime = task ? formatTime(audioEl.currentTime || 0) : "0:00";
  const totalTime = task ? formatTime(audioEl.duration || 0) : "0:00";
  const progressWidth =
    task && audioEl.duration
      ? (audioEl.currentTime / audioEl.duration) * 100
      : 0;

  const playIcon = playerState.isPlaying ? ICON_PAUSE : ICON_PLAY;
  const playTitle = playerState.isPlaying ? "Pause" : "Play";

  return `<div class="now-playing" id="now-playing">
    <div class="now-playing-cover" id="now-playing-cover">${ICON_COVER_PLACEHOLDER}</div>
    <div class="now-playing-info">
      <span class="now-playing-title">${title}</span>
      ${meta}
    </div>
    <div class="now-playing-controls">
      <button class="player-btn" id="prev-btn" title="Previous"${disabledAttr}>${ICON_PREV}</button>
      <button class="player-btn player-btn-main" id="play-btn" title="${playTitle}"${disabledAttr}>${playIcon}</button>
      <button class="player-btn" id="next-btn" title="Next"${disabledAttr}>${ICON_NEXT}</button>
    </div>
    <div class="progress-container">
      <span class="progress-time" id="current-time">${currentTime}</span>
      <div class="progress-bar" id="progress-bar">
        <div class="progress-bar-fill" id="progress-fill" style="width: ${progressWidth}%"></div>
      </div>
      <span class="progress-time" id="total-time">${totalTime}</span>
    </div>
  </div>`;
}

function renderPlaylistRow(task: Task, index: number): string {
  const isActive = task.id === playerState.currentTrackId;
  const isProcessing = task.status === "processing" || task.status === "pending";
  const isError = task.status === "error";
  const isPlayable = task.status === "complete" && task.hasAudio;

  const numberOrSpinner = isProcessing
    ? '<div class="spinner spinner-sm"></div>'
    : `<span>${index}</span>`;

  const firstClip = task.clips[0];
  const title = firstClip?.title
    ? escapeHtml(firstClip.title)
    : escapeHtml(task.prompt);

  const stegoBadge = task.stego
    ? ` <span class="badge-stego badge-stego-sm">${escapeHtml(task.stego.technique)}</span>`
    : "";

  const classes = [
    "playlist-row",
    isActive ? "playlist-row-active" : "",
    isError ? "playlist-row-error" : "",
    isPlayable ? "playlist-row-playable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return `<div class="${classes}" data-task-id="${task.id}">
    <div class="playlist-row-number">${numberOrSpinner}</div>
    <div class="playlist-row-cover" data-cover-id="${task.id}">${ICON_COVER_PLACEHOLDER}</div>
    <div class="playlist-row-info">
      <span class="playlist-row-title">${title}${stegoBadge}</span>
      <span class="playlist-row-meta">${task.id.slice(0, 8)}</span>
    </div>
    <span class="status ${getStatusClass(task.status)}">${formatStatus(task.status)}</span>
    <button class="playlist-row-kebab" data-kebab-id="${task.id}" title="More options">${ICON_KEBAB}</button>
  </div>`;
}

async function getCoverUrl(taskId: string): Promise<string | null> {
  const cached = playerState.coverUrlCache.get(taskId);
  if (cached) return cached;

  const url = await api.coverUrl(taskId);
  if (url) {
    playerState.coverUrlCache.set(taskId, url);
  }
  return url;
}

function loadCoverForElement(taskId: string, el: HTMLElement): void {
  getCoverUrl(taskId).then((url) => {
    if (url && el) {
      el.innerHTML = `<img class="cover-img" src="${url}" alt="Cover">`;
    }
  });
}

function openCoverUpload(taskId: string): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/jpeg,image/png,image/webp";
  input.addEventListener("change", async () => {
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showError("Image too large. Maximum 5MB.");
      return;
    }

    const contentType = file.type as "image/jpeg" | "image/png" | "image/webp";
    if (!["image/jpeg", "image/png", "image/webp"].includes(contentType)) {
      showError("Unsupported image format. Use JPEG, PNG, or WebP.");
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = "";
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      const b64 = btoa(binary);

      await api.uploadCover(taskId, b64, contentType);

      // Invalidate cache and reload
      const oldUrl = playerState.coverUrlCache.get(taskId);
      if (oldUrl) URL.revokeObjectURL(oldUrl);
      playerState.coverUrlCache.delete(taskId);

      const task = state.tasks.get(taskId);
      if (task) task.hasCover = true;

      renderTasks();
    } catch (err) {
      if (err instanceof ApiClientError) {
        showError(`Upload failed: ${err.message}`);
      } else {
        showError("Cover upload failed.");
      }
    }
  });
  input.click();
}

function renderTasks(): void {
  if (state.tasks.size === 0) {
    elements.tasksContainer.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18V5l12-2v13"/>
            <circle cx="6" cy="18" r="3"/>
            <circle cx="18" cy="16" r="3"/>
          </svg>
        </div>
        <p class="empty-state-text">No tracks yet. Generate your first song above!</p>
      </div>
    `;
    return;
  }

  const tasks = Array.from(state.tasks.values());
  const hasErrors = tasks.some((t) => t.status === "error");

  const currentTask = playerState.currentTrackId
    ? state.tasks.get(playerState.currentTrackId)
    : null;

  const nowPlayingHtml = renderNowPlaying(currentTask);

  const playlistHtml = tasks
    .map((task, i) => renderPlaylistRow(task, i + 1))
    .join("");

  const toolbarHtml =
    `<div class="playlist-toolbar">` +
    (hasErrors
      ? `<button class="btn btn-sm btn-danger" id="clear-errors-btn">Clear Errors</button>`
      : "") +
    `<button class="btn btn-sm btn-danger" id="clear-all-btn">Clear All</button>` +
    `</div>`;

  elements.tasksContainer.innerHTML = `${nowPlayingHtml}<div class="playlist" id="playlist">${playlistHtml}</div>${toolbarHtml}`;

  // Attach toolbar handlers
  document
    .getElementById("clear-errors-btn")
    ?.addEventListener("click", clearErrors);
  document
    .getElementById("clear-all-btn")
    ?.addEventListener("click", clearAll);

  // Lazy-load cover art
  if (playerState.currentTrackId) {
    const npCover = document.getElementById("now-playing-cover");
    if (npCover) loadCoverForElement(playerState.currentTrackId, npCover);
  }
  for (const task of tasks) {
    if (task.hasCover) {
      const el = document.querySelector(`[data-cover-id="${task.id}"]`) as HTMLElement | null;
      if (el) loadCoverForElement(task.id, el);
    }
  }

  // Update stego panel with completed tasks (including library tracks if tab active)
  if (state.connected) {
    updateCompletedTasks(getCompletedTasksForStego());
  }
}

// ---- Library Tab ----

function setTracksTab(tab: "playlist" | "library"): void {
  libraryState.activeTab = tab;
  elements.playlistTabBtn.classList.toggle("active", tab === "playlist");
  elements.libraryTabBtn.classList.toggle("active", tab === "library");
  elements.tasksContainer.classList.toggle("hidden", tab === "library");
  elements.libraryContainer.classList.toggle("hidden", tab === "playlist");

  // Lazy-load library on first switch
  if (tab === "library" && !libraryState.loaded && !libraryState.loading) {
    loadLibrary(0);
  }

  // Update stego dropdown with appropriate tracks
  if (state.connected) {
    updateCompletedTasks(getCompletedTasksForStego());
  }
}

async function loadLibrary(page: number): Promise<void> {
  if (libraryState.loading) return;
  libraryState.loading = true;

  if (page === 0) {
    elements.libraryContainer.innerHTML = `<div class="library-loading"><div class="spinner spinner-sm"></div> Loading library...</div>`;
  }

  try {
    const resp = await api.library(page);
    if (page === 0) {
      libraryState.tracks = resp.tracks;
    } else {
      libraryState.tracks.push(...resp.tracks);
    }
    libraryState.page = resp.page;
    libraryState.hasMore = resp.has_more;
    libraryState.loaded = true;
    buildImportedMap();
    renderLibrary();
  } catch (err) {
    if (err instanceof ApiClientError) {
      showError(`Library load failed: ${err.message}`);
    } else {
      showError("Failed to load Suno library.");
    }
    if (page === 0) {
      elements.libraryContainer.innerHTML = `<div class="library-empty">Failed to load library.</div>`;
    }
  } finally {
    libraryState.loading = false;
  }
}

function buildImportedMap(): void {
  libraryState.importedMap.clear();
  for (const task of state.tasks.values()) {
    if (task.clips && task.clips.length > 0) {
      for (const clip of task.clips) {
        // Cross-reference clip IDs against library tracks
        if (libraryState.tracks.some((lt) => lt.id === clip.id)) {
          libraryState.importedMap.set(clip.id, task.id);
        }
      }
    }
  }
}

function renderLibrary(): void {
  if (libraryState.tracks.length === 0) {
    elements.libraryContainer.innerHTML = `<div class="library-empty">No tracks found in your Suno library.</div>`;
    return;
  }

  const rows = libraryState.tracks
    .map((track) => renderLibraryRow(track))
    .join("");

  const loadMoreHtml = libraryState.hasMore
    ? `<div class="library-load-more"><button class="btn btn-secondary btn-sm" id="library-load-more-btn">Load More</button></div>`
    : "";

  elements.libraryContainer.innerHTML = `<div class="library-list">${rows}</div>${loadMoreHtml}`;

  // Attach Load More handler
  document.getElementById("library-load-more-btn")?.addEventListener("click", () => {
    loadLibrary(libraryState.page + 1);
  });
}

function renderLibraryRow(track: LibraryTrack): string {
  const isActive = playerState.currentTrackId === `library:${track.id}`;
  const importedTaskId = libraryState.importedMap.get(track.id);
  const importedBadge = importedTaskId
    ? ` <span class="badge-imported">imported</span>`
    : "";

  const coverHtml = track.image_url
    ? `<img src="${escapeHtml(track.image_url)}" alt="" loading="lazy">`
    : ICON_COVER_PLACEHOLDER;

  const meta = [
    track.tags,
    track.created_at ? new Date(track.created_at).toLocaleDateString() : null,
  ]
    .filter(Boolean)
    .join(" &middot; ");

  const classes = [
    "library-row",
    isActive ? "library-row-active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return `<div class="${classes}" data-library-id="${track.id}">
    <div class="library-row-cover">${coverHtml}</div>
    <div class="library-row-info">
      <span class="library-row-title">${escapeHtml(track.title)}${importedBadge}</span>
      <span class="library-row-meta">${meta}</span>
    </div>
    <div class="library-row-actions">
      <button class="library-row-kebab" data-library-kebab-id="${track.id}" title="More options">${ICON_KEBAB}</button>
    </div>
  </div>`;
}

async function playLibraryTrack(clipId: string): Promise<void> {
  const track = libraryState.tracks.find((t) => t.id === clipId);
  if (!track) return;

  const playerId = `library:${clipId}`;
  playerState.currentTrackId = playerId;

  // Check if already imported — stream from gateway
  const importedTaskId = libraryState.importedMap.get(clipId);
  if (importedTaskId) {
    let blobUrl = playerState.blobUrlCache.get(importedTaskId);
    if (!blobUrl) {
      try {
        const blob = await api.stream(importedTaskId);
        blobUrl = URL.createObjectURL(blob);
        playerState.blobUrlCache.set(importedTaskId, blobUrl);
      } catch {
        showError("Playback failed.");
        return;
      }
    }
    audioEl.src = blobUrl;
    audioEl.play();
    renderLibrary();
    return;
  }

  // Try direct Suno CDN URL
  let blobUrl = libraryState.blobUrlCache.get(clipId);
  if (!blobUrl) {
    try {
      const resp = await fetch(track.audio_url);
      if (!resp.ok) throw new Error("fetch failed");
      const blob = await resp.blob();
      blobUrl = URL.createObjectURL(blob);
      libraryState.blobUrlCache.set(clipId, blobUrl);
    } catch {
      // CORS blocked — auto-import and stream from gateway
      try {
        const result = await api.libraryImport(clipId, track.title, track.prompt);
        libraryState.importedMap.set(clipId, result.task_id);
        const blob = await api.stream(result.task_id);
        blobUrl = URL.createObjectURL(blob);
        playerState.blobUrlCache.set(result.task_id, blobUrl);
      } catch (err) {
        if (err instanceof ApiClientError) {
          showError(`Playback failed: ${err.message}`);
        } else {
          showError("Playback failed.");
        }
        return;
      }
    }
  }

  audioEl.src = blobUrl;
  audioEl.play();
  renderLibrary();
}

async function downloadLibraryTrack(clipId: string): Promise<void> {
  const track = libraryState.tracks.find((t) => t.id === clipId);
  if (!track) return;

  // If already imported, use gateway download
  const importedTaskId = libraryState.importedMap.get(clipId);
  if (importedTaskId) {
    downloadTask(importedTaskId);
    return;
  }

  try {
    const resp = await fetch(track.audio_url);
    if (!resp.ok) throw new Error("fetch failed");
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suno-${track.title.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 40)}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    // CORS blocked — import first, then download
    try {
      const result = await api.libraryImport(clipId, track.title, track.prompt);
      libraryState.importedMap.set(clipId, result.task_id);
      renderLibrary();
      downloadTask(result.task_id);
    } catch (err) {
      if (err instanceof ApiClientError) {
        showError(`Download failed: ${err.message}`);
      } else {
        showError("Download failed.");
      }
    }
  }
}

async function importLibraryTrack(clipId: string): Promise<string> {
  // Check if already imported
  const existing = libraryState.importedMap.get(clipId);
  if (existing) return existing;

  const track = libraryState.tracks.find((t) => t.id === clipId);
  const result = await api.libraryImport(
    clipId,
    track?.title,
    track?.prompt
  );

  libraryState.importedMap.set(clipId, result.task_id);

  // Add to main tasks state so it appears in "Your Tracks"
  if (!state.tasks.has(result.task_id)) {
    state.tasks.set(result.task_id, {
      id: result.task_id,
      prompt: result.title || track?.title || "Imported",
      status: "complete",
      clips: [{ id: clipId, status: "complete", audio_url: track?.audio_url, image_url: track?.image_url, title: track?.title }],
      pollCount: 0,
      hasAudio: true,
      hasCover: !!track?.image_url,
    });
    saveTasks();
  }

  renderLibrary();
  return result.task_id;
}

function openLibraryMenu(clipId: string, anchorEl: HTMLElement): void {
  closeMenu();
  playerState.menuOpenFor = `library:${clipId}`;

  const track = libraryState.tracks.find((t) => t.id === clipId);
  if (!track) return;

  const menu = document.createElement("div");
  menu.className = "track-menu";
  menu.id = "track-menu";

  // Play
  const playBtn = document.createElement("button");
  playBtn.className = "track-menu-item";
  playBtn.innerHTML = `${ICON_PLAY} Play`;
  playBtn.addEventListener("click", () => {
    closeMenu();
    playLibraryTrack(clipId);
  });
  menu.appendChild(playBtn);

  // Download
  const dlBtn = document.createElement("button");
  dlBtn.className = "track-menu-item";
  dlBtn.innerHTML = `${ICON_DOWNLOAD} Download MP3`;
  dlBtn.addEventListener("click", () => {
    closeMenu();
    downloadLibraryTrack(clipId);
  });
  menu.appendChild(dlBtn);

  // Import for stego
  const importedTaskId = libraryState.importedMap.get(clipId);
  if (!importedTaskId) {
    const importBtn = document.createElement("button");
    importBtn.className = "track-menu-item";
    importBtn.innerHTML = `${ICON_IMPORT} Import for Stego`;
    importBtn.addEventListener("click", async () => {
      closeMenu();
      try {
        await importLibraryTrack(clipId);
        showError("Track imported successfully!");
      } catch (err) {
        if (err instanceof ApiClientError) {
          showError(`Import failed: ${err.message}`);
        } else {
          showError("Import failed.");
        }
      }
    });
    menu.appendChild(importBtn);
  }

  // Position
  const rect = anchorEl.getBoundingClientRect();
  menu.style.top = `${rect.bottom + 4}px`;
  menu.style.right = `${window.innerWidth - rect.right}px`;

  document.body.appendChild(menu);
}

// API functions
async function connect(): Promise<void> {
  const apiKey = elements.apiKeyInput.value.trim();
  if (!apiKey) {
    showError("Please enter an API key");
    return;
  }

  elements.connectBtn.disabled = true;
  elements.connectBtn.innerHTML = '<div class="spinner spinner-sm"></div>';
  clearError();

  try {
    api.setApiKey(apiKey);
    const [quota, jobsResponse] = await Promise.all([
      api.quota(),
      api.jobs().catch(() => ({ jobs: [] })),
    ]);

    saveApiKey(apiKey);

    state.tasks.clear();
    for (const job of jobsResponse.jobs) {
      state.tasks.set(job.task_id, {
        id: job.task_id,
        prompt: job.prompt,
        status: job.status as StatusResponse["status"],
        clips: job.clips ?? [],
        pollCount: 0,
        hasAudio: job.has_audio,
        hasCover: job.has_cover,
        stego: job.stego,
      });
    }

    loadTasks();
    renderTasks();

    // Resume polling for incomplete tasks
    state.tasks.forEach((task) => {
      if (task.status === "processing" || task.status === "pending") {
        pollTask(task.id);
      }
    });

    updateQuota(quota.credits_remaining, quota.monthly_limit);
    setConnected(true);

    initStegoPanel(api, getCompletedTasksForStego(), { onImportLibraryTrack: importLibraryTrack });

    stopJobPoller = startJobPoller({
      api,
      getCurrentJobs,
      onChanged: handleJobsDiff,
    });
  } catch (err) {
    api.clearApiKey();
    if (err instanceof ApiClientError) {
      if (err.code === "SESSION_EXPIRED") {
        showError("Session expired. Please contact admin to refresh.");
      } else {
        showError(`Connection failed: ${err.message}`);
      }
    } else {
      showError("Connection failed. Check your API key.");
    }
  } finally {
    elements.connectBtn.disabled = false;
    elements.connectBtn.textContent = state.connected ? "Disconnect" : "Connect";
  }
}

function disconnect(): void {
  stopJobPoller?.();
  stopJobPoller = null;

  api.clearApiKey();
  clearApiKey();
  setConnected(false);

  // Clean up audio
  audioEl.pause();
  audioEl.src = "";
  playerState.currentTrackId = null;
  playerState.isPlaying = false;
  for (const blobUrl of playerState.blobUrlCache.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  playerState.blobUrlCache.clear();
  for (const coverUrl of playerState.coverUrlCache.values()) {
    URL.revokeObjectURL(coverUrl);
  }
  playerState.coverUrlCache.clear();
  stopProgressLoop();

  // Clean up library state
  for (const blobUrl of libraryState.blobUrlCache.values()) {
    URL.revokeObjectURL(blobUrl);
  }
  libraryState.blobUrlCache.clear();
  libraryState.tracks = [];
  libraryState.page = 0;
  libraryState.hasMore = true;
  libraryState.loaded = false;
  libraryState.importedMap.clear();
  setTracksTab("playlist");

  state.tasks.forEach((task) => {
    if (task.pollTimer) {
      clearTimeout(task.pollTimer);
    }
  });
  state.tasks.clear();
  saveTasks();
  renderTasks();
  elements.apiKeyInput.value = "";
}

interface GenerateParams {
  requestBody: import("./types/api.js").GenerateRequest;
  displayPrompt: string;
}

function buildGenerateRequest(): GenerateParams | null {
  const instrumental = elements.instrumentalCheck.checked;
  const model = elements.modelSelect.value || undefined;

  if (generateMode === "custom") {
    const lyrics = elements.lyricsInput.value.trim();
    const title = elements.titleInput.value.trim() || undefined;
    const style = elements.customStyleInput.value.trim() || undefined;

    if (!lyrics && !instrumental) {
      showError("Please enter lyrics or enable instrumental mode");
      return null;
    }

    // Advanced options
    const negativeTags = elements.negativeTagsInput.value.trim() || undefined;
    const vocalGender = elements.vocalGenderM.checked ? "m" as const
      : elements.vocalGenderF.checked ? "f" as const
      : undefined;
    const weirdnessVal = parseFloat(elements.weirdnessSlider.value);
    const weirdness = weirdnessVal > 0 ? weirdnessVal : undefined;

    const displayPrompt = title || lyrics.slice(0, 80) || "Instrumental";
    return {
      displayPrompt,
      requestBody: {
        prompt: displayPrompt,
        lyrics: instrumental ? undefined : lyrics || undefined,
        title, style, instrumental, model,
        negative_tags: negativeTags,
        vocal_gender: vocalGender,
        weirdness,
      },
    };
  }

  const prompt = elements.promptInput.value.trim();
  if (!prompt) {
    showError("Please enter a prompt");
    return null;
  }
  const style = elements.styleInput.value.trim() || undefined;
  return { displayPrompt: prompt, requestBody: { prompt, style, instrumental, model } };
}

function resetGenerateForm(): void {
  elements.promptInput.value = "";
  elements.styleInput.value = "";
  elements.titleInput.value = "";
  elements.lyricsInput.value = "";
  elements.customStyleInput.value = "";
  elements.instrumentalCheck.checked = false;
  elements.negativeTagsInput.value = "";
  elements.vocalGenderNone.checked = true;
  elements.weirdnessSlider.value = "0";
  elements.weirdnessValue.textContent = "0.00";
}

async function generate(): Promise<void> {
  const params = buildGenerateRequest();
  if (!params) return;

  elements.generateBtn.disabled = true;
  elements.generateBtn.innerHTML =
    '<div class="spinner spinner-sm"></div> Generating...';
  clearError();

  try {
    const response = await api.generate(params.requestBody);
    const task: Task = {
      id: response.task_id,
      prompt: params.displayPrompt,
      status: "processing",
      clips: response.clips,
      pollCount: 0,
    };

    state.tasks.set(task.id, task);
    saveTasks();
    renderTasks();
    pollTask(task.id);
    resetGenerateForm();
    refreshQuota();
  } catch (err) {
    if (err instanceof ApiClientError) {
      if (err.code === "SESSION_EXPIRED") {
        showError("Session expired. Please contact admin to refresh.");
      } else {
        showError(`Generation failed: ${err.message}`);
      }
    } else {
      showError("Generation failed. Please try again.");
    }
  } finally {
    elements.generateBtn.disabled = false;
    elements.generateBtn.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="5 3 19 12 5 21 5 3"/>
      </svg>
      Generate
    `;
  }
}

async function pollTask(taskId: string): Promise<void> {
  const task = state.tasks.get(taskId);
  if (!task || task.status === "complete" || task.status === "error") {
    return;
  }

  if (task.pollCount >= MAX_POLL_ATTEMPTS) {
    task.status = "error";
    saveTasks();
    renderTasks();
    return;
  }

  try {
    const status = await api.status(taskId);
    task.status = status.status;
    task.clips = status.clips || task.clips;
    if (status.has_audio !== undefined) {
      task.hasAudio = status.has_audio;
    }
    task.pollCount++;
    saveTasks();
    renderTasks();

    if (status.status === "processing" || status.status === "pending") {
      task.pollTimer = window.setTimeout(
        () => pollTask(taskId),
        POLL_INTERVAL_MS,
      );
    } else if (status.status === "complete" && !task.hasAudio) {
      // Status is complete but audio not yet in R2 — keep polling briefly
      task.pollTimer = window.setTimeout(
        () => pollTask(taskId),
        POLL_INTERVAL_MS,
      );
    }
  } catch (err) {
    console.error("Poll error:", err);
    task.pollTimer = window.setTimeout(
      () => pollTask(taskId),
      POLL_INTERVAL_MS,
    );
  }
}

function getCurrentJobs(): Map<string, JobSnapshot> {
  const snapshot = new Map<string, JobSnapshot>();
  for (const [id, task] of state.tasks) {
    snapshot.set(id, {
      status: task.status,
      hasAudio: !!task.hasAudio,
      hasCover: !!task.hasCover,
      stego: task.stego ?? null,
    });
  }
  return snapshot;
}

function handleJobsDiff(diff: JobsDiff): void {
  let changed = false;

  for (const job of diff.added) {
    state.tasks.set(job.task_id, {
      id: job.task_id,
      prompt: job.prompt,
      status: job.status,
      clips: job.clips ?? [],
      pollCount: 0,
      hasAudio: job.has_audio,
      hasCover: job.has_cover,
      stego: job.stego,
    });
    if (job.status === "pending" || job.status === "processing") {
      const existing = state.tasks.get(job.task_id);
      if (existing && !existing.pollTimer) {
        pollTask(job.task_id);
      }
    }
    changed = true;
  }

  for (const job of diff.updated) {
    const existing = state.tasks.get(job.task_id);
    if (existing) {
      existing.status = job.status;
      existing.clips = job.clips ?? existing.clips;
      existing.hasAudio = job.has_audio;
      existing.hasCover = job.has_cover;
      existing.stego = job.stego;
      changed = true;
    }
  }

  for (const taskId of diff.removed) {
    const blobUrl = playerState.blobUrlCache.get(taskId);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      playerState.blobUrlCache.delete(taskId);
    }
    const coverUrl = playerState.coverUrlCache.get(taskId);
    if (coverUrl) {
      URL.revokeObjectURL(coverUrl);
      playerState.coverUrlCache.delete(taskId);
    }
    if (playerState.currentTrackId === taskId) {
      audioEl.pause();
      audioEl.src = "";
      playerState.currentTrackId = null;
      playerState.isPlaying = false;
    }
    const task = state.tasks.get(taskId);
    if (task?.pollTimer) {
      clearTimeout(task.pollTimer);
    }
    state.tasks.delete(taskId);
    changed = true;
  }

  if (changed) {
    saveTasks();
    renderTasks();
  }
}

async function downloadTask(taskId: string): Promise<void> {
  try {
    const blob = await api.download(taskId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suno-${taskId.slice(0, 8)}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    if (err instanceof ApiClientError) {
      showError(`Download failed: ${err.message}`);
    } else {
      showError("Download failed. Please try again.");
    }
  }
}

async function downloadStegoTask(taskId: string): Promise<void> {
  try {
    const blob = await api.stegoDownload(taskId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suno-${taskId.slice(0, 8)}-encoded.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    if (err instanceof ApiClientError) {
      showError(`Download failed: ${err.message}`);
    } else {
      showError("Download failed. Please try again.");
    }
  }
}

async function deleteTask(taskId: string): Promise<void> {
  try {
    await api.deleteJob(taskId);

    // Clean up blob URL
    const blobUrl = playerState.blobUrlCache.get(taskId);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      playerState.blobUrlCache.delete(taskId);
    }

    // Stop playback if deleting current track
    if (playerState.currentTrackId === taskId) {
      audioEl.pause();
      audioEl.src = "";
      playerState.currentTrackId = null;
      playerState.isPlaying = false;
    }

    state.tasks.delete(taskId);
    saveTasks();
    renderTasks();
  } catch (err) {
    if (err instanceof ApiClientError) {
      showError(`Delete failed: ${err.message}`);
    } else {
      showError("Delete failed.");
    }
  }
}

async function clearErrors(): Promise<void> {
  try {
    const result = await api.bulkDeleteJobs("errors");
    for (const [id, task] of state.tasks) {
      if (task.status === "error") {
        const blobUrl = playerState.blobUrlCache.get(id);
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
          playerState.blobUrlCache.delete(id);
        }
        state.tasks.delete(id);
      }
    }
    saveTasks();
    renderTasks();
    showError(`Cleared ${result.deleted} error(s)`);
  } catch (err) {
    if (err instanceof ApiClientError) {
      showError(`Clear failed: ${err.message}`);
    } else {
      showError("Clear failed.");
    }
  }
}

async function clearAll(): Promise<void> {
  if (!confirm("Delete all tracks and audio files? This cannot be undone.")) {
    return;
  }
  try {
    const result = await api.bulkDeleteJobs("all");

    // Clean up all blob URLs
    for (const blobUrl of playerState.blobUrlCache.values()) {
      URL.revokeObjectURL(blobUrl);
    }
    playerState.blobUrlCache.clear();

    // Stop playback
    audioEl.pause();
    audioEl.src = "";
    playerState.currentTrackId = null;
    playerState.isPlaying = false;

    state.tasks.clear();
    saveTasks();
    renderTasks();
    showError(`Cleared ${result.deleted} track(s)`);
  } catch (err) {
    if (err instanceof ApiClientError) {
      showError(`Clear failed: ${err.message}`);
    } else {
      showError("Clear failed.");
    }
  }
}

async function refreshQuota(): Promise<void> {
  try {
    const quota = await api.quota();
    updateQuota(quota.credits_remaining, quota.monthly_limit);
  } catch {
    // Silently fail quota refresh
  }
}

// Event handlers
elements.apiKeyForm.addEventListener("submit", (e) => {
  e.preventDefault();
  if (state.connected) {
    disconnect();
  } else {
    connect();
  }
});

elements.generateForm.addEventListener("submit", (e) => {
  e.preventDefault();
  generate();
});

elements.simpleModeTab.addEventListener("click", () => setGenerateMode("simple"));
elements.customModeTab.addEventListener("click", () => setGenerateMode("custom"));

// Tracks tab switching
elements.playlistTabBtn.addEventListener("click", () => setTracksTab("playlist"));
elements.libraryTabBtn.addEventListener("click", () => setTracksTab("library"));

elements.instrumentalCheck.addEventListener("change", () => {
  const disabled = elements.instrumentalCheck.checked;
  elements.lyricsInput.disabled = disabled;
  elements.lyricsInput.style.opacity = disabled ? "0.4" : "1";
  elements.lyricsWizardBtn.disabled = disabled;
});

elements.weirdnessSlider.addEventListener("input", () => {
  elements.weirdnessValue.textContent = parseFloat(elements.weirdnessSlider.value).toFixed(2);
});

elements.toastDismiss.addEventListener("click", () => clearError());

// Lyrics wizard button
elements.lyricsWizardBtn.addEventListener("click", async () => {
  if (elements.lyricsWizardBtn.disabled) return;

  const prompt = elements.titleInput.value.trim() || "Write a song";

  elements.lyricsWizardBtn.disabled = true;
  elements.lyricsWizardBtn.classList.add("loading");

  try {
    const result = await api.lyrics(prompt);
    elements.lyricsInput.value = result.lyrics;
    if (result.title && !elements.titleInput.value.trim()) {
      elements.titleInput.value = result.title;
    }
  } catch (err) {
    if (err instanceof ApiClientError) {
      showError(`Lyrics generation failed: ${err.message}`);
    } else {
      showError("Lyrics generation failed. Please try again.");
    }
  } finally {
    elements.lyricsWizardBtn.disabled = elements.instrumentalCheck.checked;
    elements.lyricsWizardBtn.classList.remove("loading");
  }
});

// Style wizard button ("I'm Feeling Lucky")
elements.styleWizardBtn.addEventListener("click", () => {
  elements.customStyleInput.value = randomStyleTags();
});

// Event delegation on tasks container
elements.tasksContainer.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;

  // Now-playing controls
  if (target.closest("#play-btn")) {
    togglePlayPause();
    return;
  }
  if (target.closest("#prev-btn")) {
    prevTrack();
    return;
  }
  if (target.closest("#next-btn")) {
    nextTrack();
    return;
  }

  // Progress bar seek
  if (target.closest("#progress-bar")) {
    handleProgressClick(e as MouseEvent);
    return;
  }

  // Kebab button
  const kebab = target.closest(".playlist-row-kebab") as HTMLElement | null;
  if (kebab) {
    const taskId = kebab.dataset.kebabId;
    if (taskId) {
      if (playerState.menuOpenFor === taskId) {
        closeMenu();
      } else {
        openMenu(taskId, kebab);
      }
    }
    return;
  }

  // Playlist row click (play track)
  const row = target.closest(".playlist-row-playable") as HTMLElement | null;
  if (row) {
    const taskId = row.dataset.taskId;
    if (taskId) playTrack(taskId);
  }
});

// Event delegation on library container
elements.libraryContainer.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;

  // Kebab button
  const kebab = target.closest(".library-row-kebab") as HTMLElement | null;
  if (kebab) {
    const clipId = kebab.dataset.libraryKebabId;
    if (clipId) {
      if (playerState.menuOpenFor === `library:${clipId}`) {
        closeMenu();
      } else {
        openLibraryMenu(clipId, kebab);
      }
    }
    return;
  }

  // Row click (play track)
  const row = target.closest(".library-row") as HTMLElement | null;
  if (row) {
    const clipId = row.dataset.libraryId;
    if (clipId) playLibraryTrack(clipId);
  }
});

// Close menu on outside click
document.addEventListener("click", (e) => {
  if (playerState.menuOpenFor) {
    const target = e.target as HTMLElement;
    if (
      !target.closest("#track-menu") &&
      !target.closest(".playlist-row-kebab") &&
      !target.closest(".library-row-kebab")
    ) {
      closeMenu();
    }
  }
});

// Keyboard shortcuts
document.addEventListener("keydown", (e) => {
  // Escape to close menu
  if (e.key === "Escape" && playerState.menuOpenFor) {
    closeMenu();
    return;
  }

  // Space to toggle playback (when not focused on input)
  if (
    e.key === " " &&
    playerState.currentTrackId &&
    !(e.target as HTMLElement).closest("input, textarea, button")
  ) {
    e.preventDefault();
    togglePlayPause();
    return;
  }

  // Cmd/Ctrl + Enter to submit generate form
  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
    if (document.activeElement === elements.promptInput && state.connected) {
      e.preventDefault();
      generate();
    }
  }
});

// Initialize
audioEl = createAudioElement();
setConnected(false);

// Restore saved API key and auto-connect
const savedApiKey = loadApiKey();
if (savedApiKey) {
  elements.apiKeyInput.value = savedApiKey;
  connect();
}
